import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IonicModule } from '@ionic/angular';
import { NgxExtendedPdfViewerModule, isIOS, pdfDefaultOptions } from 'ngx-extended-pdf-viewer';
import { take } from 'rxjs';
import { environment } from 'src/environments/environment';
import { BaseHttpService } from '../../services/base-http/base-http.service';
import { UtilsService } from '../../services/utils.service';
import { copyToClipboard } from '../../utils/gestures/longPress';
import { ButtonComponent } from '../button/button.component';

@Component({
  selector: 'app-pdf-viewer-modal',
  templateUrl: './pdf-viewer-modal.component.html',
  styleUrls: ['./pdf-viewer-modal.component.scss'],
  imports: [IonicModule, NgxExtendedPdfViewerModule, ButtonComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PdfViewerModalComponent implements OnChanges {
  private _utilsService = inject(UtilsService);
  private _baseHttp = inject(BaseHttpService);
  private _destroyRef = inject(DestroyRef);

  @Input({ required: true }) url!: string;

  @Output() closed = new EventEmitter<void>();

  public readonly viewerHeight = '100%';

  public readonly pdfSrc = signal<Uint8Array | null>(null);

  private readonly _hasError = signal(false);

  private readonly _isFetching = signal(false);

  private readonly _isInitialViewReady = signal(false);

  public readonly hasError = computed(() => this._hasError());

  public readonly showLoadingIndicator = computed(
    () => !this._hasError() && (this._isFetching() || !this._isInitialViewReady()),
  );

  public readonly loadingMessage = computed(() => {
    if (this._hasError()) {
      return 'Не удалось загрузить PDF';
    }

    if (this._isFetching()) {
      return 'Загрузка документа...';
    }

    return 'Отрисовка документа...';
  });

  constructor() {
    if (isIOS()) {
      pdfDefaultOptions.disableRange = true;
      pdfDefaultOptions.disableStream = true;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['url']) {
      this._loadPdf();
    }
  }

  public close(): void {
    this.closed.emit();
  }

  public onPdfLoadingFailed(): void {
    this._hasError.set(true);
  }

  public onPageRendered(): void {
    if (!this._isInitialViewReady()) {
      this._isInitialViewReady.set(true);
    }
  }

  public async copyFileLink(): Promise<void> {
    const isCopied = await copyToClipboard(this.url);

    if (isCopied) {
      this._utilsService.openNotification('Ссылка скопирована');
      return;
    }

    this._utilsService.openNotification('Не удалось скопировать ссылку', true);
  }

  private _loadPdf(): void {
    this._resetLoadingState();
    this._isFetching.set(true);
    this.pdfSrc.set(null);

    this._baseHttp
      .getFile(this._getRelativePath(this.url))
      .pipe(take(1), takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (buffer) => {
          this.pdfSrc.set(new Uint8Array(buffer));
          this._isFetching.set(false);
        },
        error: () => {
          this._isFetching.set(false);
          this._hasError.set(true);
        },
      });
  }

  private _getRelativePath(fileUrl: string): string {
    if (fileUrl.startsWith('/')) {
      return fileUrl;
    }

    try {
      const parsedUrl = new URL(fileUrl);

      if (parsedUrl.origin === environment.befreeUrl) {
        return `${parsedUrl.pathname}${parsedUrl.search}`;
      }

      return fileUrl;
    } catch {
      return fileUrl;
    }
  }

  private _resetLoadingState(): void {
    this._hasError.set(false);
    this._isFetching.set(false);
    this._isInitialViewReady.set(false);
    this.pdfSrc.set(null);
  }
}
